import { useEffect, useReducer, useState, type Dispatch } from "react";
import { openAiLiveStream, type LiveStream } from "../../../lib/aiLive";
import { INITIAL_AI_LIVE, aiLiveReducer, type AiLiveAction, type AiLiveState } from "./aiLive";

/** Dropped connections inside `DROP_WINDOW_MS` before the stream is given a rest. */
const MAX_DROPS = 4;
const DROP_WINDOW_MS = 60_000;
/** How long a refused or flapping stream rests before one more try. */
const RETRY_MS = 60_000;
/** A hidden tab lets go of its stream after this long; coming back opens a fresh one. */
const IDLE_DISCONNECT_MS = 5 * 60_000;
/** How often ended calls are let go of, and the dock's clock moves while it shows something. */
const TICK_MS = 1_000;

/**
 * The AI calls going on now, kept true by the live stream.
 *
 * Built like the shell's counters (`SignalsProvider`), without their fallback
 * poll: a dock that cannot get its stream shows nothing, which is exactly what
 * it shows when no AI call is going on, and the rest of the CRM does not depend
 * on it.
 *
 *   DROPS. The browser reconnects a dropped stream by itself; a run of drops, or
 *   a refused stream (401, 503 at the connection cap), rests for a minute and
 *   tries once more.
 *   HIDDEN TAB. Let go after five minutes, reopened on the way back: the new
 *   snapshot holds whatever happened meanwhile. A tab opened in the background
 *   starts that count at once, not on its first visibility change.
 *
 * `now` moves once a second while there is a call to show, so a timer runs and
 * an ended call leaves the dock on time; with nothing to show it stands still.
 * A snapshot or a call event sets it too, in the same render: a tab back after
 * a while would otherwise draw a call that ended long ago for one frame.
 */
export function useAiLiveCalls(enabled: boolean): { state: AiLiveState; dispatch: Dispatch<AiLiveAction>; now: number } {
  const [state, dispatch] = useReducer(aiLiveReducer, INITIAL_AI_LIVE);
  const [now, setNow] = useState(() => Date.now());
  const hasCalls = Object.keys(state.calls).length > 0;

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let stream: LiveStream | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let idle: ReturnType<typeof setTimeout> | null = null;
    let drops: number[] = [];

    const drop = () => {
      stream?.close();
      stream = null;
      if (!stopped) dispatch({ type: "down" });
    };

    const rest = () => {
      drop();
      if (retry || stopped) return;
      retry = setTimeout(() => {
        retry = null;
        if (document.visibilityState === "visible") open();
      }, RETRY_MS);
    };

    function open(): void {
      if (stopped || stream) return;
      drops = [];
      stream = openAiLiveStream({
        onSnapshot: (snapshot) => {
          drops = [];
          if (stopped) return;
          const at = Date.now();
          setNow(at);
          dispatch({ type: "snapshot", snapshot, receivedAt: at });
        },
        onGone: (event) => {
          // A colleague took the call over: nothing more about it will arrive,
          // so the card is ended here rather than left frozen on the screen.
          if (stopped) return;
          dispatch({ type: "gone", event });
        },
        onCall: (call) => {
          if (stopped) return;
          setNow(Date.now());
          dispatch({ type: "call", call });
        },
        onTurn: (event) => {
          if (!stopped) dispatch({ type: "turn", event });
        },
        onError: (permanent) => {
          if (stopped) return;
          dispatch({ type: "down" });
          const at = Date.now();
          drops = drops.filter((t) => at - t < DROP_WINDOW_MS);
          drops.push(at);
          if (permanent || drops.length >= MAX_DROPS) rest();
        },
      });
    }

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        if (idle) {
          clearTimeout(idle);
          idle = null;
        }
        open();
        return;
      }
      if (!idle) idle = setTimeout(drop, IDLE_DISCONNECT_MS);
    };

    open();
    if (document.visibilityState !== "visible") onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibility);
      stream?.close();
      stream = null;
      if (retry) clearTimeout(retry);
      if (idle) clearTimeout(idle);
    };
  }, [enabled]);

  useEffect(() => {
    if (!hasCalls) return;
    setNow(Date.now());
    const timer = setInterval(() => {
      const at = Date.now();
      setNow(at);
      dispatch({ type: "prune", now: at });
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [hasCalls]);

  return { state, dispatch, now };
}
